import { useEffect, useRef, useState } from 'react';

/**
 * Keeps a conditionally-rendered element (modal, drawer, sheet) mounted for a
 * short window after `isOpen` flips to false, so an exit animation can play
 * before React removes it from the DOM.
 *
 * Usage:
 *   const { render, closing } = useExitTransition(isOpen);
 *   if (!render) return null;
 *   // overlay: closing ? 'animate-fade-out' : 'animate-fade-in'
 *   // panel:   closing ? 'animate-scale-out' : 'animate-scale-in'
 *
 * `durationMs` must match the exit animation length in the Tailwind config
 * (200ms for fade-out / scale-out / slide-out-down).
 */
export function useExitTransition(isOpen: boolean, durationMs = 200) {
  const [render, setRender] = useState(isOpen);
  const [closing, setClosing] = useState(false);
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => {
    const clear = () => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };

    if (isOpen) {
      clear();
      setClosing(false);
      setRender(true);
    } else if (render) {
      setClosing(true);
      clear();
      timeoutRef.current = window.setTimeout(() => {
        setRender(false);
        setClosing(false);
        timeoutRef.current = null;
      }, durationMs);
    }

    return clear;
  }, [isOpen, render, durationMs]);

  return { render, closing };
}
